import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Box,
    Grid,
    Card,
    CardContent,
    Divider,
    Button,
    Chip,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { apiClient } from '../services/api'

interface ApiTrafficDetail {
    Id: string
    ApplicationId: string | null
    Direction: string
    BrokerName: string | null
    Endpoint: string
    Method: string
    PayloadJson: string
    ResponseJson: string | null
    StatusCode: number | null
    ErrorMessage: string | null
    CreatedAt: string
}

const getStatusColor = (statusCode: number | null): 'default' | 'success' | 'error' | 'warning' => {
    if (!statusCode) return 'default'
    if (statusCode >= 200 && statusCode < 300) return 'success'
    if (statusCode >= 400 && statusCode < 500) return 'warning'
    if (statusCode >= 500) return 'error'
    return 'default'
}

const getDirectionColor = (direction: string): 'primary' | 'secondary' => {
    return direction === 'Received' ? 'primary' : 'secondary'
}

const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

const formatJson = (jsonString: string | null): string => {
    if (!jsonString) return ''
    try {
        return JSON.stringify(JSON.parse(jsonString), null, 2)
    } catch (e) {
        return jsonString
    }
}

export default function ApiTrafficDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [traffic, setTraffic] = useState<ApiTrafficDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchTraffic = async () => {
            if (!id) return

            try {
                const data = await apiClient.getApiTrafficById(id)
                setTraffic(data)
            } catch (err) {
                setError('Failed to load API traffic details')
                console.error(err)
            } finally {
                setLoading(false)
            }
        }

        fetchTraffic()
    }, [id])

    if (loading) {
        return (
            <Container maxWidth="lg">
                <CircularProgress />
            </Container>
        )
    }

    if (error || !traffic) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error || 'Traffic entry not found'}</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/api-traffic')} sx={{ mb: 2 }}>
                    Back to API Traffic
                </Button>
                <Typography variant="h4" component="h1" gutterBottom>
                    API Traffic Details
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Request Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Date/Time
                                    </Typography>
                                    <Typography variant="body1">{formatDateTime(traffic.CreatedAt)}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Direction
                                    </Typography>
                                    <Chip label={traffic.Direction} color={getDirectionColor(traffic.Direction)} size="small" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Broker
                                    </Typography>
                                    <Typography variant="body1">{traffic.BrokerName || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Application ID
                                    </Typography>
                                    <Typography variant="body1">{traffic.ApplicationId || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Endpoint
                                    </Typography>
                                    <Typography variant="body1">{traffic.Endpoint}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Method
                                    </Typography>
                                    <Chip label={traffic.Method} size="small" variant="outlined" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        Status Code
                                    </Typography>
                                    {traffic.StatusCode ? (
                                        <Chip label={traffic.StatusCode} color={getStatusColor(traffic.StatusCode)} size="small" />
                                    ) : (
                                        <Typography variant="body1">N/A</Typography>
                                    )}
                                </Grid>
                                {traffic.ErrorMessage && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            Error Message
                                        </Typography>
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                            {traffic.ErrorMessage}
                                        </Alert>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Request Payload
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Paper
                                sx={{
                                    p: 2,
                                    backgroundColor: '#f5f5f5',
                                    overflow: 'auto',
                                    maxHeight: '500px',
                                }}
                            >
                                <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '12px' }}>
                                    {formatJson(traffic.PayloadJson)}
                                </pre>
                            </Paper>
                        </CardContent>
                    </Card>
                </Grid>

                {traffic.ResponseJson && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Response Payload
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Paper
                                    sx={{
                                        p: 2,
                                        backgroundColor: '#f5f5f5',
                                        overflow: 'auto',
                                        maxHeight: '500px',
                                    }}
                                >
                                    <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '12px' }}>
                                        {formatJson(traffic.ResponseJson)}
                                    </pre>
                                </Paper>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Container>
    )
}
